import { useEffect } from 'react';

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /Loading chunk [\w-]+ failed/i,
  /ChunkLoadError/i,
];

const RELOAD_FLAG_KEY = 'rh-tools-chunk-reload';
const RELOAD_QUERY_PARAM = '__rhChunkReload';
const RELOAD_STATE_TTL_MS = 15_000;

type ReloadState = {
  count: number;
  ts: number;
};

function isChunkError(message?: string | null): boolean {
  if (!message) {
    return false;
  }
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

function safeString(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value;
  }
  if (value && typeof (value as { message?: string }).message === 'string') {
    return (value as { message?: string }).message;
  }
  try {
    return value ? String(value) : undefined;
  } catch (_error) {
    return undefined;
  }
}

function readReloadState(): ReloadState | null {
  try {
    const raw = sessionStorage.getItem(RELOAD_FLAG_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<ReloadState>;
    const count = Number(parsed.count);
    const ts = Number(parsed.ts);
    if (!Number.isFinite(count) || !Number.isFinite(ts)) {
      return null;
    }
    return { count, ts };
  } catch (_error) {
    return null;
  }
}

function writeReloadState(state: ReloadState) {
  try {
    sessionStorage.setItem(RELOAD_FLAG_KEY, JSON.stringify(state));
  } catch (_error) {
    // Ignore storage failures
  }
}

function clearReloadState() {
  try {
    sessionStorage.removeItem(RELOAD_FLAG_KEY);
  } catch (_error) {
    // Ignore storage failures
  }
}

function buildReloadUrl() {
  const url = new URL(window.location.href);
  url.searchParams.set(RELOAD_QUERY_PARAM, String(Date.now()));
  return url.toString();
}

export function useChunkLoadRecovery() {
  useEffect(() => {
    const existingReloadState = readReloadState();
    if (window.location.search.includes(RELOAD_QUERY_PARAM)) {
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete(RELOAD_QUERY_PARAM);
      window.history.replaceState(window.history.state, '', cleanUrl.toString());
    }

    let reloadTriggered = false;
    let clearTimeoutId: number | undefined;

    if (existingReloadState) {
      clearTimeoutId = window.setTimeout(() => {
        clearReloadState();
      }, RELOAD_STATE_TTL_MS);
    }

    const triggerReload = () => {
      if (reloadTriggered) {
        return;
      }
      const existingState = readReloadState();
      const now = Date.now();
      if (existingState && existingState.count >= 1 && now - existingState.ts < RELOAD_STATE_TTL_MS) {
        return;
      }
      reloadTriggered = true;
      writeReloadState({
        count: (existingState?.count ?? 0) + 1,
        ts: now,
      });
      // Use replace to avoid polluting history stack and add a cache-buster so
      // the browser fetches a fresh index after a deploy changed hashed chunks.
      window.location.replace(buildReloadUrl());
    };

    const handleErrorEvent = (event: ErrorEvent) => {
      const message = event?.message || safeString(event?.error);
      if (isChunkError(message)) {
        event.preventDefault();
        triggerReload();
      }
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      const message = safeString(event?.reason);
      if (isChunkError(message)) {
        event.preventDefault();
        triggerReload();
      }
    };

    window.addEventListener('error', handleErrorEvent);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      if (clearTimeoutId) {
        window.clearTimeout(clearTimeoutId);
      }
      window.removeEventListener('error', handleErrorEvent);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);
}
